import { useContent } from '@/content/ContentContext';
import { BriefDraft, type BriefErrorCode } from '@/domain/brief/BriefDraft';
import type { BriefWizardController } from '@/features/brief/useBriefWizard';
import { TextArea, TextInput } from '@/ui/Form/TextField';

import styles from '../Brief.module.css';

export function ContactStep({ controller }: { readonly controller: BriefWizardController }) {
  const { brief } = useContent();
  const { wizard } = controller;
  const { values, errors } = wizard;
  const errorText = (code: BriefErrorCode | undefined): string | null =>
    code ? brief.errors[code] : null;

  const summary = [
    values.projectType && brief.projectTypes[values.projectType].label,
    values.budget && brief.budgets[values.budget].label,
    values.timeline && brief.timelines[values.timeline].label,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <fieldset className={styles.step}>
      <legend className="visually-hidden">{brief.steps.contact.title}</legend>
      <div className={styles.summary}>
        <span>{summary}</span>
        <button
          type="button"
          className={styles.editButton}
          onClick={() => {
            controller.goTo('project');
          }}
        >
          {brief.actions.edit}
        </button>
      </div>

      <h3 className={styles.question} tabIndex={-1} data-step-heading>
        {brief.steps.contact.question}
      </h3>

      <div className={styles.fieldRow}>
        <TextInput
          label={brief.fields.fullName.label}
          placeholder={brief.fields.fullName.placeholder}
          autoComplete="name"
          required
          maxLength={BriefDraft.NAME_MAX_LENGTH}
          value={values.fullName}
          error={errorText(errors.fullName)}
          onChange={(event) => {
            controller.update('fullName', event.target.value);
          }}
        />
        <TextInput
          label={brief.fields.email.label}
          placeholder={brief.fields.email.placeholder}
          hint={brief.fields.email.hint}
          type="email"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          required
          value={values.email}
          error={errorText(errors.email)}
          onChange={(event) => {
            controller.update('email', event.target.value);
          }}
          onBlur={() => {
            controller.touch('email');
          }}
        />
      </div>

      <div className={styles.fieldRow}>
        <TextInput
          label={brief.fields.company.label}
          optionalLabel={brief.optional}
          placeholder={brief.fields.company.placeholder}
          autoComplete="organization"
          value={values.company}
          onChange={(event) => {
            controller.update('company', event.target.value);
          }}
        />
        <TextInput
          label={brief.fields.phone.label}
          optionalLabel={brief.optional}
          placeholder={brief.fields.phone.placeholder}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={(event) => {
            controller.update('phone', event.target.value);
          }}
        />
      </div>

      <TextArea
        label={brief.fields.message.label}
        optionalLabel={brief.optional}
        placeholder={brief.fields.message.placeholder}
        rows={4}
        maxLength={BriefDraft.MESSAGE_MAX_LENGTH}
        counter={`${values.message.length} / ${BriefDraft.MESSAGE_MAX_LENGTH}`}
        value={values.message}
        error={errorText(errors.message)}
        onChange={(event) => {
          controller.update('message', event.target.value);
        }}
      />

      {/* Piège à robots : invisible et ignoré par les humains. */}
      <div className={styles.trap} aria-hidden="true">
        <label>
          Site web
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={wizard.honeypot}
            onChange={(event) => {
              controller.setHoneypot(event.target.value);
            }}
          />
        </label>
      </div>

      <p className={styles.consent}>{brief.consent}</p>
    </fieldset>
  );
}
